/*
 * Copyright (C) 2024-2026 BIRU
 *
 * This file is part of Tenzu.
 *
 * Tenzu is free software: you can redistribute it and/or modify it
 * under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * You can contact BIRU at ask@biru.sh
 *
 */

import {
  ApplicationConfig,
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  ErrorHandler,
  importProvidersFrom,
  inject,
  InjectionToken,
  isDevMode,
  provideAppInitializer,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from "@angular/core";
import { NgEventBus } from "ng-event-bus";
import { provideRouter, RouteReuseStrategy, withComponentInputBinding, withRouterConfig } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { CustomReuseStrategy, routes } from "./app.routes";
import { TranslocoHttpLoaderService } from "@tenzu/utils/services/transloco-http-loader/transloco-http-loader.service";
import { provideTransloco } from "@jsverse/transloco";
import { provideTranslocoMessageformat } from "@jsverse/transloco-messageformat";
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { JWT_OPTIONS, JwtModule } from "@auth0/angular-jwt";
import { PRECONNECT_CHECK_BLOCKLIST } from "@angular/common";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { LanguageStore } from "@tenzu/repository/transloco";
import { httpInterceptor } from "@tenzu/utils/interceptors";
import { ConfigAppService } from "@tenzu/repository/config-app/config-app.service";
import { WsService } from "@tenzu/utils/services/ws";
import { provideTranslocoLocale } from "@jsverse/transloco-locale";
import { providePlugins } from "./providers-plugins";
import { environment } from "../environments/environment";
import { lastValueFrom } from "rxjs";
import { mockInterceptor } from "@tenzu/utils/mocks/mock.interceptor";
import { DomainMockStore } from "@tenzu/utils/mocks/mock-state";
import { MockControlPanelComponent } from "@tenzu/utils/mocks/mock-control-panel.component";

export type Plugin = object;

export const PLUGINS_TOKEN = new InjectionToken<(Plugin | null)[]>("PLUGINS_TOKEN");
export function tokenGetter() {
  return localStorage.getItem("token");
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: PLUGINS_TOKEN,
      useValue: null,
      multi: true,
    },
    ...providePlugins(),
    provideAppInitializer(async () => {
      const configAppService = inject(ConfigAppService);
      const languageStore = inject(LanguageStore);
      const wsService = inject(WsService);
      await configAppService.loadAppConfig();
      if (configAppService.config().sentry.dsn) {
        Sentry.init({
          dsn: configAppService.config().sentry.dsn,
          environment: configAppService.config().sentry.environment,
          release: environment.appVersion,
        });
      }
      wsService.init().then();
      lastValueFrom(languageStore.initLanguages()).then();
      return;
    }),
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    {
      provide: PRECONNECT_CHECK_BLOCKLIST,
      useFactory: () => {
        const configAppService = inject(ConfigAppService);
        const apiConfig = configAppService.configApi();
        if (!apiConfig) {
          throw new Error("Config API returned no configuration");
        }
        return `${apiConfig.scheme}://${apiConfig.baseDomain}`;
      },
      deps: [ConfigAppService],
    },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: "outline" } },
    importProvidersFrom(
      JwtModule.forRoot({
        jwtOptionsProvider: {
          provide: JWT_OPTIONS,
          useFactory: () => {
            const configAppService = inject(ConfigAppService);
            const apiConfig = configAppService.configApi();
            return {
              tokenGetter: tokenGetter,
              allowedDomains: [apiConfig.baseDomain],
              headerName: "Authorization",
              authScheme: "Bearer ",
            };
          },
        },
      }),
    ),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: "always" })),
    provideTransloco({
      config: {
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        availableLangs: ["en-us"],
        defaultLang: "en-us",
        fallbackLang: "en-us",
        flatten: {
          aot: !isDevMode(),
        },
      },
      loader: TranslocoHttpLoaderService,
    }),
    provideTranslocoMessageformat(),
    provideTranslocoLocale(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    provideHttpClient(
      withInterceptors([
        // mockInterceptor before httpInterceptor ; NG_MOCK_ENABLED = false literal in production => tree-shaken branch
        ...(NG_MOCK_ENABLED ? [mockInterceptor] : []),
        httpInterceptor,
      ]),
      withInterceptorsFromDi(),
    ),
    ...(NG_MOCK_ENABLED
      ? [
          provideEnvironmentInitializer(() => {
            const domainMockStore = inject(DomainMockStore);
            domainMockStore.logMockState();
          }),
          provideEnvironmentInitializer(() => {
            if (document.querySelector("app-mock-control-panel")) {
              return;
            }
            const host = document.createElement("app-mock-control-panel");
            document.body.appendChild(host);
            const componentRef = createComponent(MockControlPanelComponent, {
              environmentInjector: inject(EnvironmentInjector),
              hostElement: host,
            });
            inject(ApplicationRef).attachView(componentRef.hostView);
          }),
        ]
      : []),
    NgEventBus,
  ],
};
